import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@/lib/DatadogService';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';

// Create a module-level variable that persists across route changes but resets on page refresh
const hasNavigatedBefore = { value: false };

export const EnhancedRouteTracker: React.FC = () => {
  const location = useLocation();
  const navigationStartTimeRef = useRef(0);
  const previousUriRef = useRef<string | null>(null);

  const DEBUG_TRACKING = import.meta.env.DEV || import.meta.env.VITE_DEBUG_TRACKING === 'true';

  const siteLabels = useSelector((state: RootState) => state.page.siteLabels);

  useEffect(() => {
    const pageName = location.pathname;
    const uri = `${window.location.origin}${pageName}`;
    const queryString = location.search;
    const refUri = previousUriRef.current || '';

    const navigationType = hasNavigatedBefore.value ? 'spa' : 'pageLoad';

    // Mark that we've navigated at least once
    hasNavigatedBefore.value = true;

    if (siteLabels) {
      const container = document.getElementById('embedded-messaging');
      if (container) {
        if ([siteLabels.cartUrl, siteLabels.checkoutPageReference?.url].includes(pageName)) {
          container.style.display = 'none';
        } else {
          container.style.display = 'block';
        }
      }
    }

    // Generate a consistent operationId for this page view that will be used throughout
    const operationId = crypto.randomUUID().replace(/-/g, '');

    const start = performance.now();
    navigationStartTimeRef.current = start;

    // Get accurate page load timing for full page loads
    let pageLoadDuration = 0;

    if (navigationType === 'pageLoad' && window.performance) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navTiming = navEntries[0] as PerformanceNavigationTiming;
        pageLoadDuration = navTiming.loadEventEnd > 0 ? navTiming.loadEventEnd : performance.now();
      }
    }

    if (DEBUG_TRACKING) {
      // eslint-disable-next-line no-console
      console.log('🔄 [RouteTracker] Route change detected:', {
        path: pageName,
        url: window.location.href,
        queryString,
        refUri,
        navigationType,
        timestamp: new Date().toISOString(),
        operationId
      });
    }

    requestAnimationFrame(() => {
      const initialRenderDuration = performance.now() - start;

      if (DEBUG_TRACKING) {
        // eslint-disable-next-line no-console
        console.log('⏱️ [RouteTracker] Initial render complete:', {
          path: location.pathname,
          renderTime: `${Math.round(initialRenderDuration)}ms`,
          ...(navigationType === 'pageLoad' && pageLoadDuration > 0
            ? {
                pageLoadTime: `${Math.round(pageLoadDuration)}ms`
              }
            : {}),
          operationId,
          queryString,
          refUri,
          navigationType,
          timestamp: new Date().toISOString()
        });
      }

      // Track with Datadog
      datadogRum.addAction('InitialRender', {
        renderTime: `${Math.round(initialRenderDuration)}ms`,
        ...(navigationType === 'pageLoad' && pageLoadDuration > 0
          ? {
              pageLoadTime: `${Math.round(pageLoadDuration)}ms`
            }
          : {}),
        operationId,
        queryString,
        refUri,
        navigationType
      });

      previousUriRef.current = uri;

      // Track a broader "total experience" if needed
      let skipTotalExperienceTracked = false;

      const trackTotalPageExperience = () => {
        if (skipTotalExperienceTracked) return;
        skipTotalExperienceTracked = true;

        // Calculate total experience time correctly based on navigation type
        const totalExperienceTime =
          navigationType === 'spa'
            ? performance.now() - navigationStartTimeRef.current
            : pageLoadDuration + (performance.now() - start);

        datadogRum.addAction('PageExperienceComplete', {
          path: location.pathname,
          uri,
          duration: totalExperienceTime,
          measurementType: 'totalExperience',
          customOperationId: operationId,
          queryString,
          refUri,
          navigationType
        });
      };

      // Safety net: max wait for total experience
      const maxWaitTimeout = window.setTimeout(() => {
        if (!skipTotalExperienceTracked) {
          if (DEBUG_TRACKING) {
            // eslint-disable-next-line no-console
            console.log('⏰ [RouteTracker] Maximum wait time reached for total experience');
          }
          trackTotalPageExperience();
        }
      }, 8000);

      window.addEventListener(
        'load',
        () => {
          window.setTimeout(() => {
            if (!skipTotalExperienceTracked) {
              trackTotalPageExperience();
            }
          }, 500);
        },
        { once: true }
      );

      window.setTimeout(() => {
        if (!skipTotalExperienceTracked) {
          trackTotalPageExperience();
        }
      }, 2000);

      return () => {
        window.clearTimeout(maxWaitTimeout);
      };
    });
  }, [location.pathname, location.search, DEBUG_TRACKING, siteLabels]);

  return null;
};

export default EnhancedRouteTracker;
